"use client"

import { fr } from "@codegouvfr/react-dsfr"
import { HeaderQuickAccessItem } from "@codegouvfr/react-dsfr/Header"
import { Box, Link, Popover, Typography } from "@mui/material"
import NextLink from "next/link"
import { usePathname, useRouter } from "next/navigation"
import { useCallback, useState } from "react"

import type { WithLangAndT } from "@/app/i18n/settings"
import { useAuth } from "@/context/AuthContext"
import { apiGet } from "@/utils/api.utils"
import { PAGES } from "@/utils/routes.utils"

export function MonCompteQuickAccess({ lang, t }: WithLangAndT) {
  const { session, setSession } = useAuth()
  const { push } = useRouter()
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)

  const handleLogout = useCallback(async () => {
    await apiGet("/_private/auth/logout", {})
    setSession(null)
    push(PAGES.static.home.getPath(lang))
  }, [push, setSession, lang])

  const handleOpenPopover = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleClosePopover = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const isPopoverOpened = Boolean(anchorEl)
  const popoverId = isPopoverOpened ? "mon-compte-popover" : undefined
  const pathname = usePathname()

  if (!session) {
    return (
      <HeaderQuickAccessItem
        quickAccessItem={{
          iconId: "fr-icon-lock-line",
          text: t("pages.seConnecterInscrireCompte", { lang, ns: "global" }),
          linkProps: {
            href: PAGES.static.compteProfil.getPath(lang),
          },
        }}
      />
    )
  }

  return (
    <>
      <HeaderQuickAccessItem
        quickAccessItem={{
          iconId: "fr-icon-account-circle-fill",
          text: t("pages.compte", { lang, ns: "global" }),
          buttonProps: {
            onClick: handleOpenPopover,
            "aria-describedby": popoverId,
          },
        }}
      />

      <Popover
        id={popoverId}
        open={isPopoverOpened}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        elevation={6}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: fr.spacing("2w"),
            padding: fr.spacing("2w"),
            marginBottom: fr.spacing("2w"),
          }}
        >
          <Typography>
            <Link
              sx={{
                textDecoration: "none",
                color: pathname === PAGES.static.compteProfil.getPath(lang) ? fr.colors.decisions.text.active.blueFrance.default : fr.colors.decisions.text.mention.grey.default,
              }}
              component={NextLink}
              href={PAGES.static.compteProfil.getPath(lang)}
            >
              Clés d'API
            </Link>
          </Typography>
          <Box component="hr" sx={{ padding: 0, height: "1px" }} />
          <Typography sx={{ marginRight: fr.spacing("2w") }} color={fr.colors.decisions.text.mention.grey.default}>
            <strong>{session.user.email}</strong>
          </Typography>
          <Typography>
            <Link
              sx={{
                textDecoration: "none",
                color: fr.colors.decisions.text.mention.grey.default,
              }}
              onClick={handleLogout}
              href={PAGES.static.home.getPath(lang)}
            >
              <i className={fr.cx("fr-icon-logout-box-r-line")} />
              Déconnexion
            </Link>
          </Typography>
        </Box>
      </Popover>
    </>
  )
}
